import React, { useEffect, useState } from 'react';
import PageContainer from '../components/PageContainer';
import StatusDot from '../components/StatusDot';

const BOOL_FIELDS = [
  { key: 'visual_present', label: 'Present' },
  { key: 'visual_has_label', label: 'Has Label' },
  { key: 'visual_connected', label: 'Connected' },
  { key: 'visual_powering_on', label: 'Powering On' },
  { key: 'setup_output', label: 'Output OK' },
  { key: 'setup_parameters', label: 'Params OK' },
  { key: 'communication_feedback', label: 'Ctrl-Rm FB' }
];

export default function InstrumentAudit() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);
  const [auditors, setAuditors] = useState([]);

  const [filters, setFilters] = useState({
    location: '',
    tag: '',
    audited_by: '',
    date_from: '',
    date_to: '',
    visual_present: 'any',
    visual_has_label: 'any',
    visual_connected: 'any',
    visual_powering_on: 'any',
    setup_output: 'any',
    setup_parameters: 'any',
    communication_feedback: 'any'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.append('q', debouncedSearch);
    if (filters.location) params.append('location[]', filters.location);
    if (filters.tag) params.append('tag[]', filters.tag);
    if (filters.audited_by) params.append('audited_by[]', filters.audited_by);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    BOOL_FIELDS.forEach((f) => {
      const val = filters[f.key];
      if (val !== 'any' && val !== '') params.append(f.key, val);
    });
    params.append('page', page);
    params.append('page_size', pageSize);
    params.append('sort_by', sortBy);
    params.append('sort_dir', sortDir);
    setLoading(true);
    fetch('/api/instrument_audit.php?' + params.toString())
      .then((r) => r.json())
      .then((data) => {
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLocations(data.locations || []);
        setTags(data.tags || []);
        setAuditors(data.audited_by || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [debouncedSearch, filters, page, pageSize, sortBy, sortDir]);

  const resetFilters = () => {
    setFilters({
      location: '',
      tag: '',
      audited_by: '',
      date_from: '',
      date_to: '',
      visual_present: 'any',
      visual_has_label: 'any',
      visual_connected: 'any',
      visual_powering_on: 'any',
      setup_output: 'any',
      setup_parameters: 'any',
      communication_feedback: 'any'
    });
    setSearch('');
    setPage(1);
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.append('q', debouncedSearch);
    if (filters.location) params.append('location[]', filters.location);
    if (filters.tag) params.append('tag[]', filters.tag);
    if (filters.audited_by) params.append('audited_by[]', filters.audited_by);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    BOOL_FIELDS.forEach((f) => {
      const val = filters[f.key];
      if (val !== 'any' && val !== '') params.append(f.key, val);
    });
    params.append('sort_by', sortBy);
    params.append('sort_dir', sortDir);
    window.location = '/api/instrument_audit_export.php?' + params.toString();
  };

  const sort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  const header = (label, column) => (
    <th
      className="text-left p-2 cursor-pointer select-none whitespace-nowrap"
      onClick={() => sort(column)}
    >
      <span className="flex items-center">
        {label}
        {sortBy === column && (
          <span className="ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>
        )}
      </span>
    </th>
  );

  const healthStatus = (row) => {
    let hasNo = false;
    let hasUnknown = false;
    BOOL_FIELDS.forEach((f) => {
      const v = row[f.key];
      if (v === 0) hasNo = true;
      else if (v === null || v === undefined) hasUnknown = true;
    });
    return hasNo ? 0 : hasUnknown ? null : 1;
  };

  const healthTooltip = (row) => {
    const failed = BOOL_FIELDS.filter((f) => row[f.key] === 0).map((f) => f.label);
    return failed.length ? 'Failed: ' + failed.join(', ') : 'All checks passed';
  };

  return (
    <PageContainer>
      <div className="flex flex-col h-full">
        <div className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800 border-b rounded-b-lg">
          <div className="p-4 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm">Search</label>
              <input
                type="text"
                className="w-full p-1 rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
              />
            </div>
            <div>
              <label className="block text-sm">Location</label>
              <select
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="p-1 rounded min-w-[120px] bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600"
              >
                <option value="">All</option>
                {locations.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm">Tag</label>
              <select
                value={filters.tag}
                onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
                className="p-1 rounded min-w-[120px] bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600"
              >
                <option value="">All</option>
                {tags.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm">Audited by</label>
              <select
                value={filters.audited_by}
                onChange={(e) => setFilters({ ...filters, audited_by: e.target.value })}
                className="p-1 rounded min-w-[120px] bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600"
              >
                <option value="">All</option>
                {auditors.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm">Date from</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="p-1 rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm">Date to</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="p-1 rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600"
              />
            </div>
            {BOOL_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-sm" title={f.label}>
                  {f.label}
                </label>
                <select
                  value={filters[f.key]}
                  onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })}
                  className="p-1 rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600"
                >
                  <option value="any">Any</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
            ))}
            <div className="flex gap-2 ml-auto">
              <button className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700" onClick={resetFilters}>
                Reset Filters
              </button>
              <button className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700" onClick={exportCsv}>
                Export CSV
              </button>
              <div className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700">
                {total} results
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {loading && <div className="p-4">Loading...</div>}
          {error && <div className="p-4 text-red-500">Error loading data</div>}
          {!loading && !error && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white/10 dark:bg-gray-800/30">
                <tr>
                  <th className="p-2 text-left">Health</th>
                  {header('Instrument', 'instrument_name')}
                  {BOOL_FIELDS.map((f) => header(f.label, f.key))}
                  {header('Timestamp', 'timestamp')}
                  <th className="p-2">Photos</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="odd:bg-white/5 cursor-pointer"
                    onClick={() => setSelected(row)}
                  >
                    <td className="p-2">
                      <StatusDot value={healthStatus(row)} label={healthTooltip(row)} />
                    </td>
                    <td className="p-2">
                      <div className="font-semibold">{row.instrument_name}</div>
                      <div className="text-xs text-gray-500">
                        {row.tag_name} • {row.location_area}
                      </div>
                      <div className="text-xs">{row.audited_by}</div>
                    </td>
                    {BOOL_FIELDS.map((f) => (
                      <td key={f.key} className="p-2 text-center">
                        <StatusDot value={row[f.key]} label={f.label} />
                      </td>
                    ))}
                    <td className="p-2 whitespace-nowrap">
                      {row.timestamp && new Date(row.timestamp + 'Z').toLocaleString()}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-2">
                        {[row.instrument_photo_link, row.corrective_photo_link, row.signature_link].map((src, idx) => (
                          <div key={idx}>
                            {src ? (
                              <img
                                src={src}
                                alt="thumb"
                                className="w-14 h-14 object-cover rounded border"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLightbox(src);
                                }}
                              />
                            ) : (
                              <div className="w-14 h-14 bg-gray-300 dark:bg-gray-700 rounded" />
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-2 flex justify-between items-center border-t bg-gray-100 dark:bg-gray-800 rounded-t-lg">
          <button
            className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            Prev
          </button>
          <div>
            Page {page} of {Math.ceil(total / pageSize) || 1}
          </div>
          <button
            className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700"
            onClick={() => setPage(page + 1)}
            disabled={page * pageSize >= total}
          >
            Next
          </button>
        </div>
      </div>
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-20"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="full" className="max-h-full max-w-full" />
        </div>
      )}
      {selected && (
        <div className="fixed inset-0 z-30 flex" onClick={() => setSelected(null)}>
          <div className="flex-1" />
          <div
            className="w-96 bg-white dark:bg-gray-800 h-full overflow-auto p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-2">Details</h2>
            <div className="mb-2">ID: {selected.id}</div>
            <div className="mb-2">Audited by: {selected.audited_by}</div>
            <div className="mb-2">Location: {selected.location_area}</div>
            <div className="mb-2">Tag: {selected.tag_name}</div>
            <div className="mb-2">Timestamp: {selected.timestamp && new Date(selected.timestamp + 'Z').toLocaleString()}</div>
            <div className="mb-2 font-semibold">Statuses</div>
            <div className="grid grid-cols-2 gap-2">
              {BOOL_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <StatusDot value={selected[f.key]} label={f.label} />
                  <span className="text-sm">{f.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <div className="font-semibold">Corrective Actions</div>
              <div className="whitespace-pre-wrap text-sm">
                {selected.corrective_actions}
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              {[{src: selected.instrument_photo_link, title: 'Instrument photo'},
                {src: selected.corrective_photo_link, title: 'Corrective photo'},
                {src: selected.signature_link, title: 'Signature'}].map((img, idx) => (
                <div key={idx}>
                  <div className="text-sm mb-1">{img.title}</div>
                  {img.src ? (
                    <img
                      src={img.src}
                      alt={img.title}
                      className="w-full max-h-60 object-contain rounded border cursor-pointer"
                      onClick={() => setLightbox(img.src)}
                    />
                  ) : (
                    <div className="w-full h-32 bg-gray-300 dark:bg-gray-700 rounded" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}